import React, {useCallback, useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import type {FixedSizeList} from 'react-window';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {IAttributeTemplate} from '@netx/core-attribute/schema';
import useWindowEvent from '@netx/core-components/hooks/useWindowEvent';
import type {Theme} from '@netx/core-components/Theme';
import {type IGroupAutoListItem} from '@netx/core-user-next/schema';
import {
	GroupAutoList,
	type GroupAutoListProps,
} from '@netx/core-user-next/components/GroupAutoList';
import {i18n} from '@netx/core/i18n';
import type {PartialAttributePermission} from '../../types';
import Permissions, {type PermissionsProps} from '../VirtualizedListTrapFocus';

export interface PermissionsFormProps extends PermissionsProps {
	attributeTemplate?: IAttributeTemplate;
	containerRef?: React.RefObject<HTMLDivElement>;
	onAdd(permission: PartialAttributePermission): void;
	onClose?: () => void;
}

const styles = ({spacing}: Theme) =>
	createStyles({
		root: {},
		permissionListWrapper: {
			width: '100%',
		},
		container: {
			display: 'flex',
			flex: '1 1 auto',
			flexDirection: 'column',
			overflow: 'hidden',
		},
		input: {
			flex: '0 0 auto',
			padding: spacing(0, 2, 2, 2),
		},
	});

const PermissionsForm = ({
	classes,
	containerRef,
	onClose,
	onAdd,
	...props
}: PermissionsFormProps & WithStyles<typeof styles>) => {
	const groupRef = useRef<HTMLDivElement>(null);
	const virtualListRef = useRef<FixedSizeList | null>(null);

	const [stageHeight, setStageHeight] = useState(window.innerHeight);
	const [listHeight, setListHeight] = useState(40);
	// TODO: get this value (56) from a constant
	const [maxHeight, setMaxHeight] = useState(props.total * 56);
	const [addedAtIndex, setAddedAtIndex] = useState(-1);

	useEffect(() => {
		// TODO: get this value (56) from a constant
		setMaxHeight(props.total * 56);
	}, [props.total]);

	useWindowEvent(
		'resize',
		useCallback(() => {
			setStageHeight(window.innerHeight);
		}, []),
	);

	useEffect(() => {
		if (!groupRef.current) return;

		// TODO: find a way to make this clean
		const availableHeight =
			(containerRef?.current?.clientHeight || stageHeight) -
			68 - // footer
			groupRef.current.clientHeight -
			64 - // margin
			84; // header

		const minMeight = Math.min(availableHeight, 300);

		setListHeight(
			Math.min(
				availableHeight,
				// Prevent a jump in size between no items and 1 item
				maxHeight < 300 ? minMeight : maxHeight,
			),
		);
	}, [stageHeight, maxHeight]);

	const handleAdd = useCallback<
		Required<GroupAutoListProps<false>>['onChange']
	>(
		(event, group) => {
			if (!group) return;
			onAdd({groupId: group.value, groupName: group.label, read: true});
			setAddedAtIndex(props.total);
		},
		[onAdd, props.total],
	);

	const handleFilterGroups = useCallback(
		(groups: IGroupAutoListItem[]) =>
			groups.filter(
				(group) => !props.data.find((d) => d.groupId === group.value),
			),
		[props.data],
	);

	return (
		<div className={clsx(classes.container, classes.root)}>
			<div className={classes.input} ref={groupRef}>
				<GroupAutoList<false, true>
					clearOnClose
					disableClearable
					filterOptions={handleFilterGroups}
					label={i18n.t('i18n.addGroup')}
					multiple={false}
					variant="filled"
					onChange={handleAdd}
				/>
			</div>
			<div
				className={clsx(classes.container, classes.permissionListWrapper)}
				style={{height: props.total ? listHeight : undefined}}
			>
				<Permissions
					addedAtIndex={addedAtIndex}
					virtualListRef={virtualListRef}
					{...props}
				/>
			</div>
		</div>
	);
};

export default withStyles(styles)(PermissionsForm as any);
